import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api';
import { useAuth } from '../context/AuthContext';
import { playSound } from '../utils/soundAlerts';
import { getSocket } from '../socket';
import StatusBadge from './StatusBadge';
import * as mailSync from '../utils/mailSync';

// ── 상태바(최신글/업데이트 표시) ──────────────────────────────────────────
// 화면에 항상 떠 있는 한 줄짜리 바("상태바"). 모든 사용자가 IMS 각 메뉴에서 등록/수정/삭제한
// 사항(+업무요청)을 최신순으로 보여준다(생산전광판 제외 — App.js Layout에서 그 경로일 때만 이
// 컴포넌트 자체를 렌더링하지 않는다. 홈 화면에도 표시됨).
//  - 기본 위치: 우측하단 사내 메신저 버튼(ChatSidebarMenu.js) 바로 왼쪽, 높이는 그 버튼과 동일.
//  - 위치는 사용자가 자유롭게 끌어서 옮길 수 있다(왼쪽 끝 ⠿ 손잡이를 끌기 — localStorage 저장).
//  - 접힌 상태: 가장 최근 항목 한 줄만 보임. ▲ 버튼을 누르면 펼쳐진다.
//  - 펼친 상태: 바 위쪽으로 목록이 나타나며(최신순), 각 줄을 클릭하면 해당 메뉴로 이동해 그
//    항목의 상세 모달이 바로 열린다(각 페이지의 "activityOpen" location.state 처리와 연결).
//  - 실시간 반영: 등록/수정/삭제가 생기면 서버가 socket.io로 'activity:new' 신호를 즉시 쏴주고,
//    여기서는 그 신호를 받는 즉시(20초 폴링을 기다리지 않고) 목록을 다시 불러온다. 목록은
//    본인의 메뉴별 읽기 권한(계정관리 > 권한설정)에 맞게 서버에서 미리 걸러져서 내려온다.
//  - 점등: 남이 새로 등록/수정/삭제한 항목이 도착하면 ▲/▼ 접기버튼이 깜빡인다(펼쳐서 확인하면
//    꺼짐) — 바가 접혀있을 때도 새 활동이 있다는 걸 놓치지 않게 하기 위함.
//  - 가로 크기: 바 오른쪽 변을 끌어 조절. 세로 크기(펼친 목록 높이): 목록 패널 위쪽 변을 끌어 조절.
//  - 조절한 위치/가로/세로 크기는 모두 localStorage에 저장해 다음 접속 때도 유지된다.
const POS_KEY    = 'ims_activityfeed_pos';   // { left, top } — 바(접힌 한 줄) 좌상단 좌표
const WIDTH_KEY  = 'ims_activityfeed_width';
const HEIGHT_KEY = 'ims_activityfeed_height';
const LOCK_KEY   = 'ims_activityfeed_locked'; // '1'이면 위치 고정(드래그로 옮길 수 없음)
// 기본 크기 — 사용자가 실제로 사용하던 크기(가로 560px)를 그대로 기본값으로 적용.
const MIN_W = 260, MAX_W = 900, DEFAULT_W = 560;
const MIN_H = 120, MAX_H = 560, DEFAULT_H = 280;
// 사내 메신저 버튼(ChatSidebarMenu.js) 기준 기하값 — 세로(바닥선) 정렬에 사용.
// right:20/bottom:20, width:160, padding 6px 상하 + 아이콘 34px = 높이 46px.
const CHAT_BTN_RIGHT = 20, CHAT_BTN_BOTTOM = 20, CHAT_BTN_WIDTH = 160, CHAT_BTN_HEIGHT = 46;
const GAP = 12; // (미사용 — 이전엔 메신저 버튼 옆 배치 계산에 사용)
const BAR_H = CHAT_BTN_HEIGHT;
const POLL_MS = 20000;
// 기본 위치 — 좌측 사이드바(App.js, 고정폭 200px) 바로 오른쪽, 바닥선은 메신저 버튼과 동일.
const SIDEBAR_W = 200;
const BAR_LEFT_GAP = 16;
const BAR_LEFT_DEFAULT = SIDEBAR_W + BAR_LEFT_GAP;

function clamp(v, lo, hi) { return Math.min(hi, Math.max(lo, v)); }

// 기본 위치: 좌측 사이드바 바로 오른쪽, 바닥선(bottom)은 메신저 버튼과 동일.
function defaultPos() {
  const vh = window.innerHeight || 800;
  return {
    left: BAR_LEFT_DEFAULT,
    top: vh - CHAT_BTN_BOTTOM - CHAT_BTN_HEIGHT,
  };
}

function formatLine(item) {
  return `${item.menu_label} | ${item.title} ${item.action}`;
}

function timeAgo(raw) {
  if (!raw) return '';
  // DB의 created_at은 SQLite datetime('now','localtime')로 저장되는데, 실제 배포(Windows) 서버의
  // C런타임이 process.env.TZ='Asia/Seoul'을 인식하지 못해 사실상 UTC로 저장된다(kstDate.js의
  // toISOString() 관련 주석 참고 - 이 저장방식은 OS/CRT 타임존 설정에 의존적이라 신뢰할 수 없다).
  // 그 결과 "2026-09-12 08:53:00"처럼 타임존 표시가 없는 문자열을 브라우저가 KST로 오인해 파싱하면
  // 실제 이벤트 시각보다 약 9시간 전으로 계산되어 "8시간 전"처럼 표시된다.
  // → 타임존 표시(Z, +09:00 등)가 없는 문자열은 UTC로 간주해 'Z'를 붙여 명확하게 파싱한다.
  let s = String(raw).replace(' ', 'T');
  if (!/[Zz]$|[+-]\d{2}:?\d{2}$/.test(s)) s += 'Z';
  const t = new Date(s);
  if (isNaN(t.getTime())) return '';
  const diffMin = Math.floor((Date.now() - t.getTime()) / 60000);
  if (diffMin < 1) return '방금';
  if (diffMin < 60) return `${diffMin}분 전`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}시간 전`;
  return `${Math.floor(diffHr / 24)}일 전`;
}

export default function ActivityFeedBar() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  // 폴링마다 직전까지 확인했던 가장 큰 id를 기억해뒀다가, 그보다 큰(=새로 생긴) 항목이
  // 있으면 알림음을 울린다. null이면 "아직 한 번도 안 불러온 상태"라는 뜻이라 최초 로드
  // 시에는 울리지 않는다(접속하자마자 기존 내역 전체에 대해 알림음이 울리는 것을 방지).
  const lastMaxIdRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  // 남이 새로 등록/수정/삭제한 항목이 있는데 아직 펼쳐서 확인하지 않았으면 true — 접기버튼을
  // 깜빡이게 하는 데 쓴다. 펼치면(확인하면) 꺼진다.
  const [hasNew, setHasNew] = useState(false);
  const [width, setWidth] = useState(() => {
    const saved = Number(localStorage.getItem(WIDTH_KEY));
    return saved >= MIN_W && saved <= MAX_W ? saved : DEFAULT_W;
  });
  const [height, setHeight] = useState(() => {
    const saved = Number(localStorage.getItem(HEIGHT_KEY));
    return saved >= MIN_H && saved <= MAX_H ? saved : DEFAULT_H;
  });
  const [pos, setPos] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(POS_KEY) || 'null');
      if (saved && typeof saved.left === 'number' && typeof saved.top === 'number') return saved;
    } catch (e) { /* ignore */ }
    return defaultPos();
  });
  // 위치 고정 — 켜면 왼쪽 ⠿ 손잡이로 끌어서 옮기는 동작이 막힌다(가로/세로 크기 조절은 그대로 가능).
  const [locked, setLocked] = useState(() => localStorage.getItem(LOCK_KEY) === '1');
  const toggleLocked = () => {
    setLocked(v => {
      const next = !v;
      localStorage.setItem(LOCK_KEY, next ? '1' : '0');
      return next;
    });
  };
  const widthRef = useRef(width);
  const heightRef = useRef(height);
  const posRef = useRef(pos);
  useEffect(() => { widthRef.current = width; }, [width]);
  useEffect(() => { heightRef.current = height; }, [height]);
  useEffect(() => { posRef.current = pos; }, [pos]);
  const resizeRef = useRef(null);
  const dragRef = useRef(null);

  // 상태바에도 홈 화면과 동일하게 로그인한 본인의 현재 상태(회의중/출장중/외근중/휴가중) 배지를
  // 표시한다 — 1분마다 자동 갱신.
  const [myStatus, setMyStatus] = useState(null);
  useEffect(() => {
    const fetchMyStatus = () => {
      API.get('/user-status/me').then(r => setMyStatus(r.data?.status || null)).catch(() => {});
    };
    fetchMyStatus();
    const timer = setInterval(fetchMyStatus, 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  // 2026-09-09 — "n분 전" 표시가 실제 흐른 시간과 무관하게 마냥 고정되어 보인다는 제보 수정.
  // timeAgo()는 렌더링되는 그 순간의 Date.now()로 경과시간을 계산하는데, 지금까지는 items가
  // 바뀔 때(20초 폴링 또는 socket 'activity:new' 신호 수신 시)만 다시 렌더링됐다. 즉 한동안
  // 아무도 새 활동을 만들지 않으면(=소켓 신호도, 폴링 결과 변화도 없으면) 화면에 붙박인 "방금"/
  // "3분 전" 같은 문구가 실제로는 30분, 1시간이 지나도 그대로 남아있어, 마치 시간이 등록시점에서
  // 멈춰 있는 것처럼 보였다. 데이터 자체는 매번 최신(수정/등록 시점)이 맞게 기록되고 있었지만,
  // 화면이 그 흐른 시간을 스스로 다시 계산해서 보여주지 않았던 것 — 아래 1분 tick으로 items가
  // 그대로여도 컴포넌트를 주기적으로 다시 그려서 timeAgo()가 "지금" 기준으로 재계산되게 한다.
  // 새 메일 알람 — utils/mailSync.js가 5분마다 확인해 새 메일이 있으면 여기에 알린다.
  const [mailState, setMailState] = useState(() => mailSync.getState());
  useEffect(() => mailSync.subscribe(setMailState), []);
  useEffect(() => {
    const onNew = () => { playSound('update'); };
    window.addEventListener('ims:mail-new', onNew);
    return () => window.removeEventListener('ims:mail-new', onNew);
  }, []);

  const [, forceTimeTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => forceTimeTick(v => v + 1), 30000);
    return () => clearInterval(t);
  }, []);

  const load = useCallback(() => {
    API.get('/activity-feed', { params: { limit: 30 } })
      .then(r => {
        const rows = Array.isArray(r.data) ? r.data : [];
        setItems(rows);

        const prevMax = lastMaxIdRef.current;
        if (rows.length) {
          if (prevMax !== null) {
            // id는 서버에서 ORDER BY id DESC로 내려오므로, prevMax보다 큰 항목들이 이번에
            // 새로 생긴 것들이다. 그중 내가 직접 등록/수정/삭제한 것(actor_name === 내 이름)은
            // 제외하고, 남은 게 하나라도 있으면 알림음을 한 번 울린다.
            const hasNewFromOthers = rows.some(row => row.id > prevMax && row.actor_name !== user?.name);
            if (hasNewFromOthers) { playSound('update'); setHasNew(true); }
          }
          lastMaxIdRef.current = rows[0].id;
        }
      })
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    load();
    // 20초 폴링은 소켓 연결이 끊겼을 때를 대비한 백업 경로로 유지한다(주 경로는 아래 소켓 신호).
    const timer = setInterval(load, POLL_MS);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(timer); window.removeEventListener('focus', onFocus); };
  }, [load]);

  // 실시간 반영 — 누군가 등록/수정/삭제하면 서버가 'activity:new'를 즉시 쏴준다. 20초 폴링을
  // 기다리지 않고 바로 목록을 다시 불러온다.
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    const onActivityNew = () => load();
    socket.on('activity:new', onActivityNew);
    return () => socket.off('activity:new', onActivityNew);
  }, [load]);

  // ── 가로/세로 크기 조절 ──
  const onResizeMove = useCallback((e) => {
    const r = resizeRef.current;
    if (!r) return;
    if (r.mode === 'width') {
      setWidth(clamp(r.w0 + (e.clientX - r.x0), MIN_W, MAX_W));
    } else if (r.mode === 'height') {
      setHeight(clamp(r.h0 + (r.y0 - e.clientY), MIN_H, MAX_H));
    }
  }, []);
  const stopResize = useCallback(() => {
    const mode = resizeRef.current && resizeRef.current.mode;
    if (mode === 'width') localStorage.setItem(WIDTH_KEY, String(widthRef.current));
    if (mode === 'height') localStorage.setItem(HEIGHT_KEY, String(heightRef.current));
    resizeRef.current = null;
    window.removeEventListener('mousemove', onResizeMove);
    window.removeEventListener('mouseup', stopResize);
  }, [onResizeMove]);
  const startResize = (mode) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = { mode, x0: e.clientX, y0: e.clientY, w0: width, h0: height };
    window.addEventListener('mousemove', onResizeMove);
    window.addEventListener('mouseup', stopResize);
  };

  // ── 자유 위치 이동(드래그) ── 바 왼쪽 끝의 ⠿ 손잡이를 끌어서 어디로든 옮길 수 있다.
  // 화면 밖으로 완전히 나가지 않도록 좌표를 뷰포트 안으로 clamp한다.
  const onDragMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d) return;
    const vw = window.innerWidth, vh = window.innerHeight;
    const left = clamp(d.left0 + (e.clientX - d.x0), 0, Math.max(0, vw - 60));
    const top  = clamp(d.top0 + (e.clientY - d.y0), 0, Math.max(0, vh - BAR_H));
    setPos({ left, top });
  }, []);
  const stopDrag = useCallback(() => {
    dragRef.current = null;
    localStorage.setItem(POS_KEY, JSON.stringify(posRef.current));
    window.removeEventListener('mousemove', onDragMove);
    window.removeEventListener('mouseup', stopDrag);
  }, [onDragMove]);
  const startDrag = (e) => {
    if (locked) return; // 고정된 상태에서는 손잡이를 끌어도 움직이지 않는다.
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = { x0: e.clientX, y0: e.clientY, left0: pos.left, top0: pos.top };
    window.addEventListener('mousemove', onDragMove);
    window.addEventListener('mouseup', stopDrag);
  };

  useEffect(() => () => {
    window.removeEventListener('mousemove', onResizeMove);
    window.removeEventListener('mouseup', stopResize);
    window.removeEventListener('mousemove', onDragMove);
    window.removeEventListener('mouseup', stopDrag);
  }, [onResizeMove, stopResize, onDragMove, stopDrag]);

  const openItem = (item) => {
    setExpanded(false);
    // 2026-08-31 — 지금까지는 ▲/▼ 접기버튼을 눌러야만 점등(깜빡임)이 꺼졌다. 상태바를 클릭해
    // 상세화면으로 이동하는 경우(펼친 목록의 줄 클릭, 접힌 상태에서 최신 항목 줄 클릭)에도
    // "확인했다"고 보고 점등을 꺼준다.
    setHasNew(false);
    // 홈(menu:'home' — 근태체크/소셜 업데이트 등)은 실제 라우트가 '/home'이 아니라 '/'이므로
    // 별도 처리한다. 그 외 메뉴는 지금까지처럼 `/${menu}` 그대로 이동.
    const path = item.menu === 'home' ? '/' : `/${item.menu}`;
    navigate(path, {
      // sub_type: 한 메뉴 안에 서로 다른 테이블/id 체계를 쓰는 하위 기능(예: 출고관리의 이관출고/
      // 교환·반품, 업무관리의 업무요청)이 있을 때, 해당 페이지가 어느 목록에서 item_id를 찾아야
      // 할지 구분하는 값.
      state: { activityOpen: { itemId: item.item_id, subType: item.sub_type || null, action: item.action, ts: Date.now() } }
    });
  };

  const latest = items[0];

  return (
    <div style={{ position:'fixed', left:pos.left, top:pos.top, zIndex:3000, fontFamily:'inherit' }}>
      {/* 펼친 목록 — 바 위쪽으로 겹치지 않게 나타남(절대좌표, 컨테이너 기준점을 바꾸지 않음) */}
      {expanded && (
        <div style={{ position:'absolute', bottom:'100%', left:0, width, height, background:'#fff',
          border:'1px solid #e2e8f0', borderRadius:'10px 10px 0 0', boxShadow:'0 -6px 24px rgba(0,0,0,0.15)',
          display:'flex', flexDirection:'column', overflow:'hidden' }}>
          {/* 세로 크기 조절(위쪽 변) */}
          <div onMouseDown={startResize('height')} title="목록 높이 조절"
            style={{ position:'absolute', top:0, left:0, right:0, height:6, cursor:'ns-resize', zIndex:5 }} />
          <div style={{ padding:'8px 8px 8px 12px', borderBottom:'1px solid #f1f5f9', background:'#f8fafc',
            fontSize:12, fontWeight:700, color:'#475569', flexShrink:0,
            display:'flex', alignItems:'center', justifyContent:'space-between', gap:8 }}>
            <span>📋 최신 업데이트 현황</span>
            {/* 위치 고정 버튼 — 목록 헤더의 가장 오른쪽. 켜면 아래 ⠿ 손잡이 드래그가 막혀 실수로 위치가 바뀌지 않는다. */}
            <button onClick={toggleLocked} title={locked ? '위치 고정 해제' : '위치 고정'}
              style={{ background:'none', border:'none', color: locked ? '#eab308' : '#94a3b8',
                width:22, height:22, flexShrink:0, cursor:'pointer', fontSize:13,
                display:'flex', alignItems:'center', justifyContent:'center' }}>
              {locked ? '🔒' : '🔓'}
            </button>
          </div>
          <div style={{ flex:1, overflowY:'auto' }}>
            {items.length === 0 && (
              <div style={{ padding:'16px 12px', fontSize:12, color:'#94a3b8' }}>표시할 활동이 없습니다.</div>
            )}
            {items.map(item => (
              <div key={item.id} onClick={() => openItem(item)}
                style={{ padding:'8px 12px', fontSize:13, color:'#334155', cursor:'pointer',
                  borderBottom:'1px solid #f8fafc', display:'flex', justifyContent:'space-between', gap:8 }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1 }}>
                  {formatLine(item)}
                </span>
                <span style={{ flexShrink:0, color:'#94a3b8', fontSize:11 }}>{timeAgo(item.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 한 줄 바(접힌 상태에서도 항상 보임) — 새 활동이 도착하면 좌측의 작은 빨간 점 대신
          바 전체 배경색이 #1677ff로 깜빡인다(펼쳐서 확인하면 꺼짐). */}
      <div style={{ position:'relative', width, height:BAR_H, color:'#e2e8f0',
        borderRadius: expanded ? '0 0 10px 10px' : 10, boxShadow:'0 4px 16px rgba(0,0,0,0.2)',
        display:'flex', alignItems:'center', overflow:'hidden',
        background: (hasNew || mailState.newCount > 0) ? undefined : '#1e293b',
        animation: (hasNew || mailState.newCount > 0) ? 'activityFeedBarBlink 1s ease-in-out infinite' : 'none' }}>
        {(hasNew || mailState.newCount > 0) && (
          <style>{`@keyframes activityFeedBarBlink { 0%,100% { background-color:#1e293b; } 50% { background-color:#1677ff; } }`}</style>
        )}
        {/* 위치 이동 손잡이 — 고정 중에는 눌러도 움직이지 않는다 */}
        <div onMouseDown={startDrag} title={locked ? '위치가 고정되어 있습니다 (오른쪽 자물쇠로 해제)' : '끌어서 위치 이동'}
          style={{ width:18, height:'100%', flexShrink:0, cursor: locked ? 'default' : 'grab', display:'flex',
            alignItems:'center', justifyContent:'center', color: locked ? '#475569' : '#64748b',
            fontSize:13, userSelect:'none' }}>
          ⠿
        </div>
        {myStatus && (
          <div style={{ flexShrink:0, marginRight:6 }}>
            <StatusBadge status={myStatus} style={{ fontSize:10, padding:'1px 7px' }} />
          </div>
        )}
        <button onClick={() => { setExpanded(v => !v); setHasNew(false); }}
          title={expanded ? '접기' : (hasNew ? '새 활동이 있습니다 — 펼쳐보기' : '펼쳐보기')}
          style={{ background:'none', border:'none', color:'#e2e8f0', width:26, height:BAR_H, flexShrink:0,
            cursor:'pointer', fontSize:11, display:'flex', alignItems:'center', justifyContent:'center' }}>
          {expanded ? '▼' : '▲'}
        </button>
        {mailState.newCount > 0 && (
          <div onClick={() => { mailSync.acknowledge(); navigate('/email'); }}
            title={Object.values(mailState.perAccount).map(p => `${p.name}: ${p.count}통`).join('\n') || '새 메일'}
            style={{ flexShrink:0, marginRight:8, padding:'2px 10px', borderRadius:12, background:'#f59e0b', color:'#fff',
              fontSize:12, fontWeight:700, cursor:'pointer', boxShadow:'0 0 0 2px rgba(255,255,255,.35)' }}>
            ✉ 새 메일 {mailState.newCount}통
          </div>
        )}
        <div onClick={() => latest && openItem(latest)}
          style={{ flex:1, minWidth:0, fontSize:13, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis',
            cursor: latest ? 'pointer' : 'default', paddingRight:4 }}>
          {latest ? formatLine(latest) : '최근 등록/수정/삭제 내역이 없습니다.'}
        </div>
        {/* 가로 크기 조절(오른쪽 변) — 왼쪽·아래쪽은 고정, 오른쪽으로만 늘거나 줆 */}
        <div onMouseDown={startResize('width')} title="가로 크기 조절"
          style={{ position:'absolute', top:0, right:0, width:6, height:'100%', cursor:'ew-resize' }} />
      </div>
    </div>
  );
}
